'use client';

const steps = [
  {
    number: '01',
    title: 'Client Joins',
    description: 'A new client signs up and enters your onboarding flow.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'AI Onboarding Summary Generated',
    description: 'The AI analyses intake data and delivers a clean, structured client profile.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Programme Built',
    description: 'The AI generates a full training programme based on goals, constraints, and your coaching philosophy.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Check-In Reminders Sent',
    description: 'ECHO sends automated reminders on each client\'s check-in day. Clients fill in a quick form — no chasing required.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Weekly Review Delivered',
    description: 'NOVA reviews every response and assigns a RAG status. A structured weekly review lands in your dashboard — ready to act on.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

export default function HowItWorksSection() {
  return (
    <section
      id="how-it-works"
      className="py-24 md:py-32 px-5 md:px-8 bg-surface"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="badge badge-coral mb-4">The Full Coaching Flow</span>
          <h2 className="display-md text-foreground mb-5">
            From Sign-Up to Weekly Review —
            <br />
            <span className="gradient-text">Fully Systemised.</span>
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Your backend runs consistently every week — without you managing it manually.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-5 gap-4">
          {steps?.map((step, index) => (
            <div
              key={index}
              className="relative bg-white rounded-3xl p-6 border border-black/[0.07] hover:border-primary/30 transition-all duration-300 hover:shadow-lg text-center"
            >
              {/* Step number badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xs shadow-lg">
                  {step?.number}
                </div>
              </div>

              {/* Icon */}
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4 mt-4 mx-auto">
                {step?.icon}
              </div>

              {/* Content */}
              <h3 className="text-sm font-bold text-foreground mb-2 leading-snug">{step?.title}</h3>
              <p className="text-xs text-muted leading-relaxed">{step?.description}</p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="text-center mt-10">
          <p className="text-muted font-semibold">Your backend runs consistently every week.</p>
        </div>
      </div>
    </section>
  );
}